import { AxiosError, AxiosResponse } from "axios";
import appConfig from "@/config/app.config";
import { ApiClient, handleError, API } from "../utils/httpService";

export interface ActivityResponse {
  success: boolean;
  data: {
    content: Activity[];
    totalPages: number;
    totalElements: number;
  };
  message?: string;
}

interface NearMe {
  activities: Activity[];
  country: string;
  description: string;
  region: string;
  title: string;
}

export interface NearMeActivityResponse {
  success: boolean;
  data: NearMe[];
  message?: string;
}
export interface CommentResponse {
  success: boolean;
  data: {
    content: Comment[];
    page: number;
    totalItems: number;
    limit: number;
  };
  message?: string;
}
interface ApiResponse<T = any> {
  data: T;
  success: boolean;
  message?: string;
}

export const getPaginatedActivities = async (
  type: string = "",
  params: any = "",
  page: number = 1
): Promise<ActivityResponse> => {
  const api = new ApiClient();

  const endpoint = type ? `/events/${type}` : "/events/";

  return api.request<ActivityResponse>({
    method: "get",
    url: `${endpoint}?page=${page}`,
    params,
  });

  try {
    const endpoint = type ? `/events/${type}` : "/events/";
    const param = params ? `${params}` : "";
    const res: AxiosResponse = await API.get(
      `${endpoint}?page=${page}${param}`
    );
    return res.data;
  } catch (error: any) {
    // console.log(error);

    // handleError(error);
    return (
      error.response?.data || {
        success: false,
        message: "Network error",
      }
    );
  }
};

export const getFilteredActivities = async (
  page: number = 1,
  params: any = ""
): Promise<ActivityResponse> => {
  const api = new ApiClient();

  return api.request<ActivityResponse>({
    method: "get",
    url: `/events/filter?page=${page}`,
    params,
  });
};

export const getActivitiesByCityRecommendation = async (
  page: number = 1,
  params: any = ""
): Promise<any> => {
  const api = new ApiClient();

  return api.request<any>({
    method: "get",
    url: `/events/categoryForMe?page=${page}&limit=30`,
    params,
  });
};

export const getHappeningActivities = async (
  page: number = 1,
  params: any = ""
): Promise<ActivityResponse> => {
  const api = new ApiClient();

  return api.request<ActivityResponse>({
    method: "get",
    url: `/events/happening?page=${page}`,
    params,
  });
};

export const getCompetitionActivities = async (
  page: number = 1,
  params: any = ""
): Promise<ActivityResponse> => {
  const api = new ApiClient();

  return api.request<ActivityResponse>({
    method: "get",
    url: `/events/competitions?page=${page}`,
    params,
  });
};

export const getAvailableRegionActivities = async (
  params: any = ""
): Promise<ApiResponse> => {
  const api = new ApiClient();

  return api.request<ApiResponse>({
    method: "get",
    url: `/events/availableRegions`,
    params,
  });
};

export const getNearbyActivities = async (
  params: any = ""
): Promise<NearMeActivityResponse> => {
  const api = new ApiClient();

  return api.request<NearMeActivityResponse>({
    method: "get",
    url: `/events/nearMe`,
    params,
  });
};

export const getSearchActivities = async (
  limit: number = 30,
  searchTerm: string = "",
  page: number = 1
): Promise<ActivityResponse> => {
  try {
    const res: AxiosResponse = await API.get(
      `events/query?page=${page}&limit=${limit}&searchTerm=${searchTerm}`
    );
    return res.data;
  } catch (error: any) {
    // console.log(error);

    // handleError(error);
    return (
      error.response?.data || {
        success: false,
        message: "Network error",
      }
    );
  }
};

export const getActivity = async (
  activityId: string
): Promise<ApiResponse<Activity>> => {
  try {
    // Use absolute URL for server components
    const url = `${appConfig.API_URL}/events/${activityId}/details`;
    // console.log("Fetching from:", url);

    const res = await fetch(url, {
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        // Add auth headers if needed
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    return await res.json();
  } catch (error: any) {
    // console.error("Fetch error:", error);
    return {
      success: false,
      message: error.message || "Failed to fetch activity",
      data: error.response?.data || null,
    };
  }
};

export const getPaginatedActivitiesByCategory = async (
  category: string = "",
  page: number = 1,
  region: string = ""
): Promise<ActivityResponse> => {
  try {
    const res: AxiosResponse = await API.get(
      `/events/category/${category}?page=${page}&region=${region}`
    );
    return res.data;
  } catch (error: any) {
    handleError(error);
    return (
      error.response?.data || {
        success: false,
        message: "Network error",
      }
    );
  }
};

export const getSystemInterests = async (): Promise<
  ApiResponse<Interest[]>
> => {
  try {
    const res: AxiosResponse<ApiResponse<Interest[]>> = await API.get(
      `/general/interests`
    );
    return res.data;
  } catch (error: any) {
    handleError(error);
    return (
      error.response?.data || {
        success: false,
        message: "Network error",
      }
    );
  }
};

interface CreateInterestPayload {
  name: string;
}

interface CommentPayload {
  comments: string;
  rating: number;
}

export const createSystemInterest = async (
  payload: CreateInterestPayload
): Promise<ApiResponse<Interest>> => {
  try {
    const res: AxiosResponse<ApiResponse<Interest>> = await API.post(
      `/general/interests`,
      payload
    );
    return res.data;
  } catch (error: any) {
    handleError(error);
    return (
      error.response?.data || {
        success: false,
        message: "Network error",
      }
    );
  }
};

export const getUserAttending = async (
  activityId: string = "",
  userId: string = ""
): Promise<ApiResponse> => {
  try {
    const res: AxiosResponse = await API.get(
      `/events/${activityId}/attendant/${userId}`
    );
    return res.data;
  } catch (error: any) {
    handleError(error);
    return (
      error.response?.data || {
        success: false,
        message: "Network error",
      }
    );
  }
};

export const getUserActivityTicket = async (
  activityId: string,
  userId: string
): Promise<ApiResponse> => {
  try {
    const res: AxiosResponse = await API.get(
      `/events/${activityId}/ticket/${userId}`
    );
    return res.data;
  } catch (error: any) {
    handleError(error);
    return (
      error.response?.data || {
        success: false,
        message: "Network error",
      }
    );
  }
};

export const getTicketDateTime = async (
  activityId: string = "",
  date: string = ""
): Promise<ApiResponse> => {
  try {
    const res: AxiosResponse = await API.get(
      `/events/${activityId}/availability?date=${date}`
    );
    return res.data;
  } catch (error: any) {
    handleError(error);
    return (
      error.response?.data || {
        success: false,
        message: "Network error",
      }
    );
  }
};

export const getPaginatedActivityComments = async (
  eventId: string,
  limit: number = 30,
  page: number = 1
): Promise<CommentResponse> => {
  try {
    const res: AxiosResponse = await API.get(
      `/events/${eventId}/comments?limit=${limit}&page=${page}`
    );
    return res.data;
  } catch (error: any) {
    // console.log(error);

    // handleError(error);
    return (
      error.response?.data || {
        success: false,
        message: "Network error",
      }
    );
  }
};

export const postComment = async (
  eventId: string,
  payload: CommentPayload
): Promise<ApiResponse<Comment>> => {
  try {
    const res: AxiosResponse<ApiResponse<Comment>> = await API.put(
      `/events/${eventId}/rateAndComment`,
      payload
    );
    return res.data;
  } catch (error: any) {
    handleError(error);
    return (
      error.response?.data || {
        success: false,
        message: "Network error",
      }
    );
  }
};

export const getRecommendedActivities = async (
  params: any = {}
  // limit: number = 10,
  // country: string = "",
  // interestGroup: string[] = [],
  // category: string = "",
  // search_term: string = "",
  // eventType: string = ""
): Promise<any> => {
  const api = new ApiClient(process.env.NEXT_PUBLIC_ACTIVITY_RECOMMEND);

  return api.request<any>({
    method: "get",
    url: "/event/recommend",
    params,
  });
};

export const addMultipleParticipants = async (
  competitionId: string,
  payload: ParticipantPayload
): Promise<ApiResponse<Comment>> => {
  try {
    const res: AxiosResponse<ApiResponse<Comment>> = await API.put(
      `events/competitions/${competitionId}/addParticipants`,
      payload
    );
    return res.data;
  } catch (error: any) {
    handleError(error);
    return (
      error.response?.data || {
        success: false,
        message: "Network error",
      }
    );
  }
};
